/**
 * What, if anything, to do about an `error` event off the OpenAI data channel.
 *
 * The default is deliberately "nothing". An error event arriving on an *open*
 * data channel is, by definition, proof that the transport is alive — the
 * authoritative "this call is dead" signal is RTCPeerConnection.connectionState,
 * which already drives scheduleReconnect in store.js. Reconnecting on an error
 * event means tearing down a healthy call, and since a backend that produces
 * errors tends to keep producing them, it burns the 5-attempt backoff budget
 * and ends at `reconnect.gaveUp` — turning a cosmetic problem into a dead voice
 * agent.
 *
 * Classification keys on `error.type`, never on a list of `error.code`s. Codes
 * are OpenAI's to add at will; types are a small, stable set. A new code inside
 * a known type then inherits the right behaviour automatically, and a genuinely
 * new type falls into the safe default and announces itself in Sentry rather
 * than silently changing what the app does. `code` is used only as a telemetry
 * key and for the one narrow "expired" family below.
 */

export const OPENAI_ERROR_ACTION = {
  // Record it and move on.
  IGNORE: 'ignore',
  // Record it and tell the user the assistant is unavailable, but do NOT
  // reconnect — for quota/rate problems a reconnect makes things strictly worse.
  IGNORE_LOUD: 'ignore-loud',
  // Hand it to the existing backoff.
  RECONNECT: 'reconnect',
};

// A Realtime session has a hard maximum duration. When it expires the agent
// goes permanently silent while the peer connection can sit in `connected`
// indefinitely, so nothing else in the app will ever notice. This is the only
// error worth reconnecting on.
const EXPIRED_PATTERN = /expired|session_ended|invalid_session/i;
const QUOTA_PATTERN = /rate.?limit|quota|insufficient_quota/i;

/**
 * @param {object} event - the raw `error` event from the data channel.
 * @returns {{ action: string, code: string, type: string, message: string }}
 */
export function classifyOpenAiError(event) {
  const error = event?.error ?? {};
  const code = String(error.code ?? 'unknown');
  const type = String(error.type ?? 'unknown');
  const message = String(error.message ?? '');

  const describe = action => ({ action, code, type, message });

  if (EXPIRED_PATTERN.test(code) || EXPIRED_PATTERN.test(type)) {
    return describe(OPENAI_ERROR_ACTION.RECONNECT);
  }

  if (QUOTA_PATTERN.test(code) || QUOTA_PATTERN.test(type)) {
    return describe(OPENAI_ERROR_ACTION.IGNORE_LOUD);
  }

  // `conversation_already_has_active_response` lands here. It means the backend
  // sent a second response.create while one was in flight; OpenAI rejects the
  // second and lets the first finish. Nothing about the audio path is affected,
  // and the frontend cannot have caused it — the data channel is receive-only
  // and nothing in frontEnd/ ever calls dataChannel.send. The fix belongs in
  // the backend, serializing its response.create calls.
  if (type === 'invalid_request_error') {
    return describe(OPENAI_ERROR_ACTION.IGNORE);
  }

  return describe(OPENAI_ERROR_ACTION.IGNORE);
}

/**
 * Repeated server errors with no audio since the first one is the one
 * behaviour-based escalation: a code list would go stale, but "OpenAI keeps
 * erroring and we have heard nothing" means the same thing whatever the codes
 * are called.
 *
 * @param {Array<{ at: number, type: string }>} recentErrors - the error ring.
 * @param {number|null} lastAudioAt - timestamp of the last output_audio_buffer.started.
 * @param {number} windowMs
 * @param {number} now
 */
export function shouldReconnectOnRepeatedServerErrors(recentErrors, lastAudioAt, windowMs, now) {
  const inWindow = recentErrors.filter(
    entry => entry.type === 'server_error' && now - entry.at <= windowMs
  );

  if (inWindow.length < 3) {
    return false;
  }

  // Audio has arrived since the run of errors started — whatever they were
  // about, the agent is still talking.
  return lastAudioAt === null || lastAudioAt < inWindow[0].at;
}
